// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Licences entreprise — les deux faces du même objet :
//   • `ent_*`    : administration des licences (Admin → Clients Entreprise)
//   • `portal_*` : espace client, vu par le license_admin du client
//   • `invite_*` : parcours public d'acceptation d'une invitation
//
// ⚠️ `ent_role_*` (Membre / Admin licence) et `license_role_*` (Utilisateur /
// Administrateur licence) désignent LA MÊME chose avec des libellés différents,
// selon qu'on est côté Admin ou côté client. Doublon antérieur à la découpe,
// conservé tel quel : les unifier est une décision produit.
//
// ⚠️ Les libellés de formulaire (`ent_form_*`) portent les unités et les règles
// de facturation (€/an, €/analyse, €/page, pages/mois). Les modifier sans
// vérifier `quotas-billing.md` et le calcul côté serveur induit le client en
// erreur sur ce qu'il paie.

const entreprise = {
  fr: {
    // ── Administration des licences ──
    ent_page_title:             'Licences Entreprise',
    ent_list_empty:             'Aucune licence entreprise',
    ent_code:                   'Code',
    ent_nom:                    'Nom',
    ent_status:                 'Statut',
    ent_seats:                  'Sièges',
    ent_date_fin:               'Date de fin',
    ent_create:                 'Créer une licence',
    ent_edit:                   'Modifier',
    ent_delete:                 'Supprimer',
    ent_deactivate:             'Désactiver',
    ent_activate:               'Réactiver',
    ent_upload_logo:            'Téléverser un logo',
    ent_delete_logo:            'Supprimer le logo',
    ent_logo_hint:              'Logo horizontal, fond transparent de préférence — idéalement 480 × 160 px. JPEG, PNG ou WebP, 2 Mo max. Il est automatiquement redimensionné.',
    ent_active_label:           'Active',
    ent_inactive_label:         'Inactive',
    ent_saving:                 'Enregistrement…',

    // ── Onglets et sièges ──
    ent_tab_infos:              'Informations',
    ent_tab_seats:              'Sièges',
    ent_tab_invitations:        'Invitations',
    ent_seats_active:           'Actifs',
    ent_seats_revoked:          'Révoqués',
    ent_seats_unlimited:        'Illimité',
    ent_edit_seat:              'Modifier le siège',
    ent_revoke_user:            'Révoquer',
    ent_restore_user:           'Restaurer',
    ent_err_update_seat:        'Erreur modification siège',

    // ── Invitations (côté Admin) ──
    ent_invite_user:            'Inviter un utilisateur',
    ent_invite_email_label:     'Email *',
    ent_inv_pending:            'En attente',
    ent_inv_cancel:             "Annuler l'invitation",
    ent_inv_accepted_on:        'Acceptée le',
    ent_inv_expires_on:         'expire le',
    ent_inv_expired_on:         'Expirée le',
    ent_inv_resend:             "Renvoyer l'invitation",
    ent_inv_resent:             'Invitation renvoyée : un nouveau lien, valable 7 jours, vient de partir.',
    ent_cancel_invite_confirm:  'Annuler cette invitation ?',

    // ── Rôles et droits ──
    ent_role_label:             'Rôle',
    ent_role_member:            'Membre',
    ent_role_admin:             'Admin licence',
    ent_rights_label:           'Droits',
    ent_rights_ocr:             'OCR',
    ent_rights_projects:        'Projets',
    ent_rights_annotations:     'Annotations',
    ent_rights_ratings:         'Évaluations',
    license_role_member:        'Utilisateur',
    license_role_admin:         'Administrateur licence',
    // Même rôles, dits pour un Cabinet (palier du forfait, pas un contrat Entreprise)
    license_role_member_cabinet: 'Membre du cabinet',
    license_role_admin_cabinet:  'Administrateur du cabinet',
    ent_badge_cabinet:           'Cabinet',
    ent_rights_cabinet_note:     'Droits fixés par le forfait Cabinet, identiques pour chaque siège.',
    ent_cabinet_verrouille:      'Ce Cabinet est un forfait souscrit en ligne : son pool, ses sièges, l\'OCR, ses tarifs et son activation suivent l\'abonnement Stripe et ne se modifient pas ici. Son nom est choisi par son titulaire.',

    // ── Formulaire de licence (unités et facturation) ──
    ent_form_nom:               'Nom commercial',
    ent_form_numero:            'N° de licence',
    ent_form_contact_email:     'Email de contact',
    ent_form_pool_count:        "Pool d'analyses",
    ent_form_pool_type:         'Période',
    ent_form_garde_fou:         'Plafond de dépassement (% de la redevance)',
    ent_garde_fou_aucun:        'Aucun plafond',
    ent_form_seats_max:         'Sièges max (vide = illimité)',
    ent_form_email_domains:     'Domaines email autorisés (séparés par virgule)',
    ent_form_mfa_required:      'MFA obligatoire',
    ent_form_date_debut:        'Date de début',
    ent_form_date_fin:          'Date de fin',
    ent_form_ocr_mode:          'Mode OCR',
    ent_form_licence_annuelle:  'Licence annuelle (€/an)',
    ent_form_overage_price:     'Prix dépassement analyse (€/analyse)',
    ent_form_ocr_pool_cap:      'Volume pool OCR (pages/mois)',
    ent_form_ocr_annual_cap:    'Plafond OCR annuel (pages)',
    ent_form_ocr_overage_price: 'Prix dépassement OCR (€/page)',
    ent_form_ocr_page_price:    'Prix OCR (€/page)',
    ent_form_setup_onboarding:  'Setup onboarding (€, one-shot)',
    ent_monthly:                'Mensuel',
    ent_annual:                 'Annuel',
    // ── Identité légale de facturation ──
    ent_fact_section:           'Facturation',
    ent_fact_raison_sociale:    'Raison sociale (dénomination légale)',
    ent_fact_siret:             'SIRET (14 chiffres)',
    ent_fact_tva:               'N° TVA intracommunautaire',
    ent_fact_adresse:           'Adresse de facturation',
    ent_fact_cp:                'Code postal',
    ent_fact_ville:             'Ville',
    ent_fact_pays:              'Pays (code ISO)',
    ent_fact_email:             'Email du service comptable',
    ent_fact_frequence:         'Fréquence de facturation',
    ent_fact_tva_taux:          'Taux de TVA (%)',
    ent_fact_majoration:        'Majoration négociée (%, vide = tarif standard)',
    ent_fact_freq_annual:       'Annuelle',
    ent_fact_freq_quarterly:    'Trimestrielle (+2 %)',
    ent_fact_freq_monthly:      'Mensuelle (+3 %)',
    ent_fact_aide_majoration:   'Le paiement fractionné majore la redevance : +2 % au trimestre, +3 % au mois. Laisser vide applique ce tarif.',
    // ── Onglet Factures ──
    ent_tab_invoices:           'Factures',
    ent_facture_new_echeance:       'Générer l’échéance',
    ent_facture_new_regul:          'Générer la régularisation',
    ent_facture_empty:              'Aucune facture pour cette licence.',
    ent_facture_col_period:         'Période',
    ent_facture_col_type:           'Type',
    ent_facture_col_amount:         'Montant TTC',
    ent_facture_col_status:         'Statut',
    ent_facture_col_number:         'N° de facture',
    ent_facture_type_echeance:      'Échéance',
    ent_facture_type_regul:         'Régularisation',
    ent_facture_status_draft:       'Brouillon',
    ent_facture_status_sent:        'Émise',
    ent_facture_status_paid:        'Payée',
    ent_facture_status_cancelled:   'Annulée',
    ent_facture_prepare:            'Préparer le proforma',
    ent_facture_status_prepared:    'Proforma prêt',
    ent_facture_proforma:           'Voir le proforma',
    ent_facture_not_prepared:       'Préparez d’abord le proforma et relisez-le.',
    ent_facture_already_prepared:   'Le proforma a déjà été préparé.',
    ent_facture_prepared_hint:      'Proforma créé chez QONTO — aucun numéro légal consommé, il reste supprimable.',
    ent_facture_send:               'Finaliser et émettre',
    ent_facture_cancel:     'Annuler le brouillon',
    ent_facture_pdf:                'Télécharger le PDF',
    ent_facture_pdf_pending:        'Le PDF est encore en cours de génération, réessayez dans un instant.',
    // ⚠️ `{ttc}` et `{client}` sont substitués dans AdminEnterpriseClients.js.
    ent_facture_confirm_send:       'Émettre définitivement cette facture de {ttc} à {client} ?\n\nCette action attribue un numéro de votre séquence légale et ne peut pas être annulée : une erreur devra faire l’objet d’un avoir.',
    ent_facture_confirm_cancel:     'Annuler ce brouillon ? Il n’a jamais été transmis au client.',
    ent_facture_nothing_to_bill:    'Rien à facturer sur cette période.',
    ent_facture_already_covered:    'Une facture non annulée couvre déjà cette période.',
    ent_facture_qonto_missing:      'QONTO n’est pas configuré sur ce serveur : la facture reste en brouillon.',
    ent_facture_sent_ok:            'Facture émise.',
    ent_ocr_disabled:           'Désactivé',
    ent_ocr_monthly:            'Plafond mensuel',
    ent_ocr_annual:             'Plafond annuel',
    ent_ocr_per_page:           'À la page',
    pool_type_monthly:          'mensuel',
    pool_type_annual:           'annuel',

    // ── Confirmations ──
    ent_confirm_deactivate:     'Désactiver cette licence ? Les utilisateurs seront bloqués à la prochaine connexion.',
    ent_confirm_activate:       'Réactiver cette licence ?',
    ent_confirm_hard_delete:    'Supprimer définitivement cette licence ? Cette action est irréversible.',

    // ── Portail client ──
    portal_title:               'Espace client',
    portal_license_label:       'Licence',
    portal_tab_seats:           'Sièges',
    portal_tab_invitations:     'Invitations',
    portal_tab_stats:           'Statistiques',
    portal_tab_content:         'Annotations & Évaluations',
    portal_pool_used:           'Analyses utilisées',
    portal_pool_remaining:      'Restantes',
    portal_pool_period:         'Période',
    portal_seats_used:          'Sièges utilisés',
    portal_seats_max:           'Maximum',
    portal_invite_btn:          'Inviter',
    portal_cabinet_rights_included: "Chaque siège de votre forfait Cabinet comprend l'OCR des rapports scannés, le mode Projets et la notation des analyses.",
    // Identité d'un Cabinet (15/09/2026) — nom obligatoire, logo facultatif.
    cabinet_nom_titre:          'Donnez un nom à votre cabinet',
    cabinet_nom_texte:          "Ce nom s'affiche chez chacun de vos collègues et dans les invitations qu'ils reçoivent. Vous pourrez le modifier à tout moment depuis votre espace client.",
    cabinet_nom_label:          'Nom du cabinet',
    cabinet_nom_placeholder:    'Ex. : Cabinet Durand Diagnostics',
    cabinet_nom_valider:        'Enregistrer',
    cabinet_identite_titre:     'Identité du cabinet',
    cabinet_identite_texte:     'Le nom et le logo sont affichés à tous les membres de votre cabinet.',
    cabinet_identite_enregistree: 'Modification enregistrée.',
    portal_revoke_confirm:      "Révoquer ce siège ? L'utilisateur sera déconnecté immédiatement et ne pourra plus se connecter. Ses analyses et son historique sont conservés.",
    // Effacement RGPD d'un siège par Evalens (5 août 2026) — sur demande écrite
    // de l'entreprise. Volontairement distinct de la révocation, qui ne détruit rien.
    ent_seat_purge:             'Effacer les données',
    ent_seat_purge_title:       'Effacer définitivement les données de ce siège',
    ent_seat_purge_intro:       "Réservé à une demande écrite de l'entreprise cliente. Analyses, projets, documents déposés, annotations et évaluations seront détruits, ainsi que les fichiers correspondants sur les serveurs.",
    ent_seat_purge_irreversible: "Cette action est irréversible. La révocation, elle, ne détruit rien et se défait d'un clic — préférez-la si le besoin est seulement de couper l'accès.",
    ent_seat_purge_confirm_label: 'Saisissez',
    ent_seat_purge_phrase:      'SUPPRIMER LES DONNEES',
    ent_seat_purge_confirm_hint: 'pour confirmer',
    ent_seat_purge_cta:         'Effacer définitivement',
    ent_seat_purge_loading:     'Effacement…',
    ent_seat_purge_done:        'Données effacées.',
    portal_restore_confirm:     'Réactiver ce siège ? La personne pourra de nouveau se connecter et occupera une place de la licence.',
    portal_stats_total:         'Total analyses',
    portal_stats_period:        'Ce mois',
    portal_stats_all_members:   'Toute l\'équipe',
    portal_no_seats:            'Aucun utilisateur sur cette licence',
    portal_no_invites:          'Aucune invitation en attente',
    portal_no_stats:            'Aucune statistique disponible',
    portal_pending_invites:     'Invitations en attente',
    portal_filtre_membre_consommations: "Filtre les consommations uniquement. Les résultats des analyses ne se consultent jamais par personne.",
    portal_siege_inactif:       'Inactif depuis 60 jours',
    portal_siege_inactif_aide:  "Aucune connexion depuis plus de 60 jours. Vous pouvez révoquer ce siège pour libérer une place ; ses analyses sont conservées.",
    portal_pool_alert_prefix:   'Pool à',
    portal_pool_alert_suffix:   'de la consommation prévue — anticipez un éventuel dépassement.',
    // `{n}` et `{prix}` substitués dans LicensePortal.js. Constat, pas conseil.
    portal_pool_depasse_singular:      '{n} analyse au-delà de la consommation prévue.',
    portal_pool_depasse_plural:        '{n} analyses au-delà de la consommation prévue.',
    portal_pool_depasse_prix_singular: "{n} analyse au-delà de la consommation prévue, facturée {prix} € HT l'unité.",
    portal_pool_depasse_prix_plural:   "{n} analyses au-delà de la consommation prévue, facturées {prix} € HT l'unité.",
    portal_garde_fou_atteint:          "Le plafond de dépassement prévu par votre contrat est atteint : les nouvelles analyses sont suspendues. Contactez Evalens pour les rétablir (contact@normascan.fr).",
    ent_no_annotations:         'Aucune annotation',
    ent_no_ratings:             'Aucune évaluation',
    team_reply_label:           'Réponse équipe',

    // ── Organisation : échelons et sous-quotas ──
    // Le serveur ne connaît que 3 niveaux (pays, region, agence) et refuse un
    // échelon plus large sous un plus fin. Les libellés suivent cet ordre.
    portal_tab_org:             'Organisation',
    org_level_pays:             'Pays',
    org_level_region:           'Région',
    org_level_agence:           'Agence',
    org_create:                 'Créer un échelon',
    org_edit:                   "Modifier l'échelon",
    org_name:                   'Nom',
    org_level:                  'Type',
    org_parent:                 'Rattaché à',
    org_parent_root:            'Directement à la licence',
    org_quota:                  "Quota d'analyses",
    org_quota_hint:             'Laisser vide pour un simple regroupement, sans plafond.',
    org_quota_none:             'Sans plafond',
    org_members:                'Membres',
    org_usage:                  'Consommation',
    org_empty:                  'Aucun échelon. Créez-en un pour répartir le pool entre vos équipes.',
    org_delete_confirm:         'Supprimer cet échelon ?',
    org_parent_locked:          'Le rattachement ne se modifie pas après création.',
    portal_seat_org:            'Échelon',
    portal_seat_org_none:       'Non rattaché',
    portal_seat_quota:          'Quota individuel',
    portal_seat_quota_hint:     'Laisser vide pour ne pas plafonder ce membre.',
    portal_stats_all_org:       'Tous les échelons',
    org_back_to_tree:           "↩ Revenir à l'arborescence",
    portal_members_direct_hint: "Seuls les membres rattachés directement à cet échelon sont proposés. Pour ceux d'une agence, sélectionnez l'agence.",
    portal_search_placeholder:  'Rechercher (email, échelon, contenu)…',

    // ── Acceptation d'invitation (parcours public) ──
    invite_loading:                'Vérification du lien…',
    invite_invalid_title:          'Lien invalide ou expiré',
    invite_invalid_link:           "Ce lien d'invitation n'est plus valide.",
    invite_back_to_login:          'Retour à la connexion',
    invite_title:                  'Créer votre compte',
    invite_subtitle_prefix:        'Vous avez été invité(e) à rejoindre',
    invite_email_label:            'Email',
    invite_password_label:         'Choisissez un mot de passe',
    invite_confirm_password_label: 'Confirmer le mot de passe',
    invite_passwords_mismatch:     'Les mots de passe ne correspondent pas',
    invite_password_too_short:     'Le mot de passe doit contenir au moins 8 caractères',
    invite_error_generic:          'Une erreur est survenue, veuillez réessayer',
    invite_submitting:             'Création en cours…',
    invite_submit:                 'Créer mon compte et accéder à Normascan',
    invite_cgu_prefix:             "J'ai lu et j'accepte les",
    invite_cgu_terms:              'CGU/CGV',
    invite_cgu_and:                'et la',
    invite_cgu_privacy:            'Politique de confidentialité',
    invite_cgu_required:           'Vous devez accepter les CGU/CGV pour créer votre compte',

    // ── Fin de droits (licence suspendue/expirée, abonnement résilié) ──
    access_warning_title:          'Accès restreint',
    access_warning_license_missing:  "Votre licence n'est plus active. Contactez votre administrateur.",
    access_warning_license_inactive: 'Votre licence a été suspendue. Vous ne pouvez plus lancer de nouvelle analyse. Contactez votre administrateur.',
    access_warning_license_expired:  'Votre licence a expiré. Vous ne pouvez plus lancer de nouvelle analyse. Contactez votre administrateur.',
    access_warning_subscription_ended: 'Votre abonnement est résilié. Vous ne pouvez plus lancer de nouvelle analyse. Réabonnez-vous pour reprendre.',
    access_warning_subscription_past_due: 'Le dernier paiement de votre abonnement a échoué. Les analyses sont suspendues jusqu’à sa régularisation, depuis « Gérer mon abonnement ».',
    access_warning_cabinet_payment_failed: 'Le paiement de l’abonnement de votre cabinet a échoué. Les analyses reprendront dès qu’il sera régularisé par son titulaire. Vos rapports restent consultables.',
    // ⚠️ `{date}` est une substitution faite dans AppLayout.js, à conserver telle quelle.
    access_warning_license_expiring_soon: 'Votre licence expire le {date}. Passé cette date, votre équipe ne pourra plus se connecter — contactez votre administrateur pour la renouveler.',
    access_warning_cgv_a_accepter: 'Nos conditions générales ont changé. Acceptez leur nouvelle version pour lancer de nouvelles analyses.',
  },

  en: {
    // ── License administration ──
    ent_page_title:             'Enterprise Licenses',
    ent_list_empty:             'No enterprise licenses',
    ent_code:                   'Code',
    ent_nom:                    'Name',
    ent_status:                 'Status',
    ent_seats:                  'Seats',
    ent_date_fin:               'End date',
    ent_create:                 'Create license',
    ent_edit:                   'Edit',
    ent_delete:                 'Delete',
    ent_deactivate:             'Deactivate',
    ent_activate:               'Re-activate',
    ent_upload_logo:            'Upload logo',
    ent_delete_logo:            'Remove logo',
    ent_logo_hint:              'Horizontal logo, ideally with a transparent background — 480 × 160 px recommended. JPEG, PNG or WebP, 2 MB max. It is resized automatically.',
    ent_active_label:           'Active',
    ent_inactive_label:         'Inactive',
    ent_saving:                 'Saving…',

    // ── Tabs and seats ──
    ent_tab_infos:              'Information',
    ent_tab_seats:              'Seats',
    ent_tab_invitations:        'Invitations',
    ent_seats_active:           'Active',
    ent_seats_revoked:          'Revoked',
    ent_seats_unlimited:        'Unlimited',
    ent_edit_seat:              'Edit seat',
    ent_revoke_user:            'Revoke',
    ent_restore_user:           'Restore',
    ent_err_update_seat:        'Error updating seat',

    // ── Invitations (Admin side) ──
    ent_invite_user:            'Invite user',
    ent_invite_email_label:     'Email *',
    ent_inv_pending:            'Pending',
    ent_inv_cancel:             'Cancel invitation',
    ent_inv_accepted_on:        'Accepted on',
    ent_inv_expires_on:         'expires on',
    ent_inv_expired_on:         'Expired on',
    ent_inv_resend:             'Resend invitation',
    ent_inv_resent:             'Invitation resent: a new link, valid for 7 days, is on its way.',
    ent_cancel_invite_confirm:  'Cancel this invitation?',

    // ── Roles and rights ──
    ent_role_label:             'Role',
    ent_role_member:            'Member',
    ent_role_admin:             'License admin',
    ent_rights_label:           'Rights',
    ent_rights_ocr:             'OCR',
    ent_rights_projects:        'Projects',
    ent_rights_annotations:     'Annotations',
    ent_rights_ratings:         'Ratings',
    license_role_member:        'User',
    license_role_admin:         'License administrator',
    license_role_member_cabinet: 'Cabinet member',
    license_role_admin_cabinet:  'Cabinet administrator',
    ent_badge_cabinet:           'Cabinet',
    ent_rights_cabinet_note:     'Rights set by the Cabinet plan, the same for every seat.',
    ent_cabinet_verrouille:      'This Cabinet is a plan subscribed online: its pool, seats, OCR, pricing and activation follow the Stripe subscription and cannot be changed here. Its name is chosen by its owner.',

    // ── License form (units and billing) ──
    ent_form_nom:               'Client name',
    ent_form_numero:            'License number',
    ent_form_contact_email:     'Contact email',
    ent_form_pool_count:        'Analysis pool',
    ent_form_pool_type:         'Period',
    ent_form_garde_fou:         'Overage cap (% of the licence fee)',
    ent_garde_fou_aucun:        'No cap',
    ent_form_seats_max:         'Max seats (empty = unlimited)',
    ent_form_email_domains:     'Allowed email domains (comma-separated)',
    ent_form_mfa_required:      'MFA required',
    ent_form_date_debut:        'Start date',
    ent_form_date_fin:          'End date',
    ent_form_ocr_mode:          'OCR mode',
    ent_form_licence_annuelle:  'Annual license fee (€/year)',
    ent_form_overage_price:     'Analysis overage price (€/analysis)',
    ent_form_ocr_pool_cap:      'OCR pool volume (pages/month)',
    ent_form_ocr_annual_cap:    'Annual OCR cap (pages)',
    ent_form_ocr_overage_price: 'OCR overage price (€/page)',
    ent_form_ocr_page_price:    'OCR price (€/page)',
    ent_form_setup_onboarding:  'Onboarding setup (€, one-shot)',
    ent_monthly:                'Monthly',
    ent_annual:                 'Annual',
    // ── Legal billing identity ──
    ent_fact_section:           'Billing',
    ent_fact_raison_sociale:    'Registered company name',
    ent_fact_siret:             'SIRET (14 digits)',
    ent_fact_tva:               'EU VAT number',
    ent_fact_adresse:           'Billing address',
    ent_fact_cp:                'Postcode',
    ent_fact_ville:             'City',
    ent_fact_pays:              'Country (ISO code)',
    ent_fact_email:             'Accounting department email',
    ent_fact_frequence:         'Billing frequency',
    ent_fact_tva_taux:          'VAT rate (%)',
    ent_fact_majoration:        'Negotiated surcharge (%, empty = standard rate)',
    ent_fact_freq_annual:       'Annual',
    ent_fact_freq_quarterly:    'Quarterly (+2%)',
    ent_fact_freq_monthly:      'Monthly (+3%)',
    ent_fact_aide_majoration:   'Splitting payments increases the fee: +2% quarterly, +3% monthly. Leave empty to apply that standard rate.',
    // ── Invoices tab ──
    ent_tab_invoices:           'Invoices',
    ent_facture_new_echeance:       'Generate instalment',
    ent_facture_new_regul:          'Generate reconciliation',
    ent_facture_empty:              'No invoice for this license.',
    ent_facture_col_period:         'Period',
    ent_facture_col_type:           'Type',
    ent_facture_col_amount:         'Amount incl. VAT',
    ent_facture_col_status:         'Status',
    ent_facture_col_number:         'Invoice no.',
    ent_facture_type_echeance:      'Instalment',
    ent_facture_type_regul:         'Reconciliation',
    ent_facture_status_draft:       'Draft',
    ent_facture_status_sent:        'Issued',
    ent_facture_status_paid:        'Paid',
    ent_facture_status_cancelled:   'Cancelled',
    ent_facture_prepare:            'Prepare proforma',
    ent_facture_status_prepared:    'Proforma ready',
    ent_facture_proforma:           'View proforma',
    ent_facture_not_prepared:       'Prepare and review the proforma first.',
    ent_facture_already_prepared:   'The proforma has already been prepared.',
    ent_facture_prepared_hint:      'Proforma created in QONTO — no legal number used, it can still be deleted.',
    ent_facture_send:               'Finalise and issue',
    ent_facture_cancel:     'Discard draft',
    ent_facture_pdf:                'Download PDF',
    ent_facture_pdf_pending:        'The PDF is still being generated, please try again shortly.',
    // ⚠️ `{ttc}` and `{client}` are substituted in AdminEnterpriseClients.js.
    ent_facture_confirm_send:       'Permanently issue this {ttc} invoice to {client}?\n\nThis assigns a number from your legal sequence and cannot be undone: a mistake will require a credit note.',
    ent_facture_confirm_cancel:     'Discard this draft? It was never sent to the client.',
    ent_facture_nothing_to_bill:    'Nothing to bill for this period.',
    ent_facture_already_covered:    'A non-cancelled invoice already covers this period.',
    ent_facture_qonto_missing:      'QONTO is not configured on this server: the invoice stays a draft.',
    ent_facture_sent_ok:            'Invoice issued.',
    ent_ocr_disabled:           'Disabled',
    ent_ocr_monthly:            'Monthly cap',
    ent_ocr_annual:             'Annual cap',
    ent_ocr_per_page:           'Per page',
    pool_type_monthly:          'monthly',
    pool_type_annual:           'annual',

    // ── Confirmations ──
    ent_confirm_deactivate:     'Deactivate this license? Users will be blocked at next login.',
    ent_confirm_activate:       'Re-activate this license?',
    ent_confirm_hard_delete:    'Permanently delete this license? This action cannot be undone.',

    // ── Client portal ──
    portal_title:               'Client portal',
    portal_license_label:       'License',
    portal_tab_seats:           'Seats',
    portal_tab_invitations:     'Invitations',
    portal_tab_stats:           'Statistics',
    portal_tab_content:         'Annotations & Ratings',
    portal_pool_used:           'Analyses used',
    portal_pool_remaining:      'Remaining',
    portal_pool_period:         'Period',
    portal_seats_used:          'Seats used',
    portal_seats_max:           'Maximum',
    portal_invite_btn:          'Invite',
    portal_cabinet_rights_included: 'Every seat on your Cabinet plan includes OCR for scanned reports, Projects mode and analysis rating.',
    cabinet_nom_titre:          'Name your firm',
    cabinet_nom_texte:          'This name is shown to each of your colleagues and in the invitations they receive. You can change it at any time from your customer area.',
    cabinet_nom_label:          'Firm name',
    cabinet_nom_placeholder:    'e.g. Durand Diagnostics',
    cabinet_nom_valider:        'Save',
    cabinet_identite_titre:     'Firm identity',
    cabinet_identite_texte:     'The name and logo are shown to every member of your firm.',
    cabinet_identite_enregistree: 'Change saved.',
    portal_revoke_confirm:      'Revoke this seat? The user will be signed out immediately and will no longer be able to sign in. Their analyses and history are kept.',
    // GDPR erasure of a seat by Evalens (5 August 2026) — on written request.
    ent_seat_purge:             'Erase data',
    ent_seat_purge_title:       'Permanently erase this seat\'s data',
    ent_seat_purge_intro:       'Reserved for a written request from the client company. Analyses, projects, uploaded documents, annotations and ratings will be destroyed, along with the matching files on the servers.',
    ent_seat_purge_irreversible: 'This action is irreversible. Revoking, by contrast, destroys nothing and can be undone in one click — prefer it if you only need to cut off access.',
    ent_seat_purge_confirm_label: 'Type',
    // ⚠️ VOLONTAIREMENT en français, y compris en anglais : le serveur compare la
    // saisie à ce littéral exact (`admin_delete_license_seat_data`).
    ent_seat_purge_phrase:      'SUPPRIMER LES DONNEES',
    ent_seat_purge_confirm_hint: 'to confirm',
    ent_seat_purge_cta:         'Erase permanently',
    ent_seat_purge_loading:     'Erasing…',
    ent_seat_purge_done:        'Data erased.',
    portal_restore_confirm:     'Restore this seat? The person will be able to sign in again and will take up one of the license seats.',
    portal_stats_total:         'Total analyses',
    portal_stats_period:        'This month',
    portal_stats_all_members:   'Whole team',
    portal_no_seats:            'No users on this license',
    portal_no_invites:          'No pending invitations',
    portal_no_stats:            'No statistics available',
    portal_pending_invites:     'Pending invitations',
    portal_filtre_membre_consommations: 'Filters usage only. Analysis results are never viewed per person.',
    portal_siege_inactif:       'Inactive for 60 days',
    portal_siege_inactif_aide:  'No sign-in for more than 60 days. You can revoke this seat to free up a place; its analyses are kept.',
    portal_pool_alert_prefix:   'Pool at',
    portal_pool_alert_suffix:   'of the expected usage — plan ahead for a possible overage.',
    portal_pool_depasse_singular:      '{n} analysis beyond the expected usage.',
    portal_pool_depasse_plural:        '{n} analyses beyond the expected usage.',
    portal_pool_depasse_prix_singular: '{n} analysis beyond the expected usage, billed €{prix} excl. tax per unit.',
    portal_pool_depasse_prix_plural:   '{n} analyses beyond the expected usage, billed €{prix} excl. tax each.',
    portal_garde_fou_atteint:          'The overage cap set by your contract has been reached: new analyses are suspended. Contact Evalens to restore them (contact@normascan.fr).',
    ent_no_annotations:         'No annotations',
    ent_no_ratings:             'No ratings',

    // ── Organisation: org units and sub-quotas ──
    portal_tab_org:             'Organisation',
    org_level_pays:             'Country',
    org_level_region:           'Region',
    org_level_agence:           'Branch',
    org_create:                 'Create an org unit',
    org_edit:                   'Edit org unit',
    org_name:                   'Name',
    org_level:                  'Type',
    org_parent:                 'Reports to',
    org_parent_root:            'Directly to the license',
    org_quota:                  'Analysis quota',
    org_quota_hint:             'Leave empty for grouping only, with no cap.',
    org_quota_none:             'No cap',
    org_members:                'Members',
    org_usage:                  'Usage',
    org_empty:                  'No org unit yet. Create one to share the pool across your teams.',
    org_delete_confirm:         'Delete this org unit?',
    org_parent_locked:          'The parent cannot be changed after creation.',
    portal_seat_org:            'Org unit',
    portal_seat_org_none:       'Unassigned',
    portal_seat_quota:          'Individual quota',
    portal_seat_quota_hint:     'Leave empty to leave this member uncapped.',
    portal_stats_all_org:       'All org units',
    org_back_to_tree:           '↩ Back to tree view',
    portal_members_direct_hint: 'Only members attached directly to this org unit are listed. For those in a branch office, select that office.',
    portal_search_placeholder:  'Search (email, org unit, content)…',
    team_reply_label:           'Team reply',

    // ── Invitation acceptance (public flow) ──
    invite_loading:                'Verifying link…',
    invite_invalid_title:          'Invalid or expired link',
    invite_invalid_link:           'This invitation link is no longer valid.',
    invite_back_to_login:          'Back to login',
    invite_title:                  'Create your account',
    invite_subtitle_prefix:        'You have been invited to join',
    invite_email_label:            'Email',
    invite_password_label:         'Choose a password',
    invite_confirm_password_label: 'Confirm password',
    invite_passwords_mismatch:     'Passwords do not match',
    invite_password_too_short:     'Password must be at least 8 characters',
    invite_error_generic:          'An error occurred, please try again',
    invite_submitting:             'Creating account…',
    invite_submit:                 'Create my account and access Normascan',
    invite_cgu_prefix:             'I have read and accept the',
    invite_cgu_terms:              'Terms of Service',
    invite_cgu_and:                'and the',
    invite_cgu_privacy:            'Privacy Policy',
    invite_cgu_required:           'You must accept the Terms of Service to create your account',

    // ── End of entitlement (license suspended/expired, subscription cancelled) ──
    access_warning_title:          'Restricted access',
    access_warning_license_missing:  'Your license is no longer active. Please contact your administrator.',
    access_warning_license_inactive: 'Your license has been suspended. You can no longer start a new analysis. Please contact your administrator.',
    access_warning_license_expired:  'Your license has expired. You can no longer start a new analysis. Please contact your administrator.',
    access_warning_subscription_ended: 'Your subscription has been cancelled. You can no longer start a new analysis. Subscribe again to resume.',
    access_warning_subscription_past_due: 'Your last subscription payment failed. Analyses are suspended until it is settled, from "Manage subscription".',
    access_warning_cabinet_payment_failed: 'Your firm’s subscription payment failed. Analyses will resume as soon as its holder settles it. Your reports remain available.',
    // ⚠️ `{date}` is substituted in AppLayout.js — keep the placeholder as is.
    access_warning_license_expiring_soon: 'Your license expires on {date}. After that date your team will no longer be able to sign in — contact your administrator to renew it.',
    access_warning_cgv_a_accepter: 'Our terms and conditions have changed. Accept the new version to start new analyses.',
  },
};

export default entreprise;
